import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";

import { useGetMiniMapDataQuery, useGetModelRevisionsQuery } from "apis/procore/procoreApi";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useSceneId } from "hooks/useSceneId";
import { selectProcoreContext } from "slices/explorer";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import { fetchDrawingData } from "./fetchDrawingData";
import { setDrawingData } from "./minimapSlice";

const l = log.miniMap.utils;

/**
 * Subscribe to the mini map data query for the active scene.
 *
 * Implementation notes:
 *
 *   1. Two-query chain: `useGetModelRevisionsQuery` (latest: true) resolves
 *      the head revision ID, then `useGetMiniMapDataQuery` fetches plans →
 *      locations → drawings for that revision. Both use `skipToken` until
 *      their inputs are ready. The sequential dependency (revision before
 *      plans) is inherent — plans require a revision ID — so the two-query
 *      structure reflects reality rather than adding a waterfall.
 *
 *   2. The slice's `extraReducers` matcher mirrors the `getMiniMapData` cache
 *      into slice state, so existing selectors keep working without changes.
 *
 *   3. The per-drawing image for the *current* drawing is prefetched here
 *      because `handleCameraUpdated` (in `miniMapWeb` / `miniMapMobile`)
 *      only kicks off a fetch when the *target* drawing differs from the
 *      current one, so it never fetches the initial drawing on mount. The
 *      effect runs whenever `data` is replaced — i.e. on first successful
 *      fetch and on every subsequent refetch (e.g. `forceMiniMapRefresh`).
 *      The matcher resets per-drawing asyncState back to `Initial` on each
 *      fulfilled action, so we always have a fresh slot to populate.
 *
 * Lives in its own file (rather than `miniMapUtils.tsx`) so that pure-utility
 * consumers — and their tests — do not transitively pull in `procoreApi`,
 * which would create a circular import via `procoreClient → app/store`.
 */
export const useMiniMapData = () => {
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneId = useSceneId();
    const dispatch = useAppDispatch();

    const baseArgs =
        procoreContext?.companyId && procoreContext.projectId && procoreContext.sceneVersionId && sceneId
            ? {
                  companyId: procoreContext.companyId,
                  projectId: procoreContext.projectId,
                  sceneId,
                  sceneVersionId: procoreContext.sceneVersionId,
              }
            : null;

    const { data: revisions } = useGetModelRevisionsQuery(baseArgs ? { ...baseArgs, latest: true } : skipToken);
    const modelRevisionId = revisions?.[0]?.id;

    const { data } = useGetMiniMapDataQuery(
        baseArgs && modelRevisionId
            ? { companyId: baseArgs.companyId, projectId: baseArgs.projectId, modelRevisionId }
            : skipToken,
    );

    useEffect(() => {
        if (!data?.currentDrawingId) {
            return;
        }
        const currentDrawing = data.drawings[data.currentDrawingId];
        if (!currentDrawing) {
            return;
        }
        const drawingId = currentDrawing.id;
        const url = currentDrawing.drawingData.url;
        const controller = new AbortController();

        dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Loading } }));
        fetchDrawingData(url, controller.signal)
            .then((imgBitmap) => {
                if (controller.signal.aborted) {
                    return;
                }
                l.debug("Successfully fetched drawing data for mini map:", drawingId, imgBitmap);
                dispatch(
                    setDrawingData({
                        drawingId,
                        asyncState: { status: AsyncStatus.Success, data: imgBitmap },
                    }),
                );
            })
            .catch((error) => {
                if (controller.signal.aborted) {
                    return;
                }
                const e = error as Error;
                l.error("Failed to fetch drawing data for mini map:", e);
                telemetry.trackCaughtException({
                    source: "miniMap.useMiniMapData.fetchDrawingData",
                    category: "render",
                    error,
                    severity: "error",
                    extraAttributes: { drawingId },
                });
                dispatch(
                    setDrawingData({
                        drawingId,
                        asyncState: { status: AsyncStatus.Error, msg: e.message },
                    }),
                );
            });

        return () => controller.abort();
    }, [data, dispatch]);
};
